import { useState } from 'react';
import type { BenchmarkRun } from '../../types/benchmark';

interface ModelConfigCardProps {
  run: BenchmarkRun;
}

export function ModelConfigCard({ run }: ModelConfigCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Get LLM config values - prioritize llm_config if available, fallback to run.model_* for backwards compatibility
  const config = run.llm_config;
  const modelName = config?.model_name ?? run.model_name ?? '-';
  const modelTemperature = config?.model_temperature ?? run.model_temperature;
  const modelMaxTokens = config?.model_max_tokens ?? run.model_max_tokens;
  const modelTopP = config?.model_top_p ?? run.model_top_p;
  const modelPresencePenalty = config?.model_presence_penalty ?? run.model_presence_penalty;
  const modelFrequencyPenalty = config?.model_frequency_penalty ?? run.model_frequency_penalty;
  const modelSeed = config?.model_seed ?? run.model_seed;

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg">
      {/* Header - Always visible */}
      <div
        className="py-3 px-4 cursor-pointer flex items-center justify-between hover:bg-gray-100 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          ⚙️ Configuration
        </h2>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${
            isExpanded ? 'transform rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Expandable content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
        {/* LLM Config Name */}
        {config && (
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">Config LLM</div>
            <div className="text-sm font-mono text-gray-900">{config.name}</div>
            <div className="text-xs text-gray-500 mt-0.5">v{config.version}</div>
          </div>
        )}

        {/* Model Name */}
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1">Modèle</div>
          <div className="text-sm font-mono text-gray-900">{modelName}</div>
          {run.model_version && (
            <div className="text-xs text-gray-500 mt-0.5">{run.model_version}</div>
          )}
        </div>

        {/* Temperature */}
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1">Temperature</div>
          <div className="text-sm font-mono text-gray-900">{modelTemperature ?? '-'}</div>
        </div>

        {/* Max Tokens */}
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1">Max Tokens</div>
          <div className="text-sm font-mono text-gray-900">{modelMaxTokens?.toLocaleString() ?? '-'}</div>
        </div>

        {/* Top P */}
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1">Top P</div>
          <div className="text-sm font-mono text-gray-900">{modelTopP ?? '-'}</div>
        </div>

        {/* Presence Penalty */}
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1">Presence Penalty</div>
          <div className="text-sm font-mono text-gray-900">{modelPresencePenalty ?? '-'}</div>
        </div>

        {/* Frequency Penalty */}
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1">Frequency Penalty</div>
          <div className="text-sm font-mono text-gray-900">{modelFrequencyPenalty ?? '-'}</div>
        </div>

        {/* Seed */}
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1">Seed</div>
          <div className="text-sm font-mono text-gray-900">{modelSeed ?? '-'}</div>
        </div>

        {/* Prompt Info */}
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1">Prompt</div>
          {run.prompt ? (
            <>
              <div className="text-sm font-mono text-gray-900">{run.prompt.name}</div>
              <div className="text-xs text-gray-500 mt-0.5">v{run.prompt.version}</div>
            </>
          ) : (
            <div className="text-sm font-mono text-gray-900">v{run.prompt_version}</div>
          )}
        </div>

        {/* Schema Info */}
        {run.schema && (
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">Schéma</div>
            <div className="text-sm font-mono text-gray-900">{run.schema.name}</div>
            <div className="text-xs text-gray-500 mt-0.5">v{run.schema.version}</div>
          </div>
        )}
      </div>
        </div>
      )}
    </div>
  );
}
